import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const EFFECTIVE = "July 2026";

// NOTE: This is a starting TEMPLATE that reflects how SalesTap works. It is not
// legal advice. Have it reviewed by counsel and replace the bracketed items
// (company legal name, contact email, governing jurisdiction) before relying on it.

function Section({ title, children }) {
  return (
    <div className="space-y-1.5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function LegalShell({ open, onOpenChange, title, subtitle, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Effective {EFFECTIVE} · {subtitle}</DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-4">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export function PrivacyPolicyDialog({ open, onOpenChange }) {
  return (
    <LegalShell open={open} onOpenChange={onOpenChange} title="Privacy Policy" subtitle="SalesTap">
      <Section title="Overview">
        <p>
          SalesTap is an AI‑powered sales analysis platform that connects to your CRM (e.g., HubSpot)
          in a <strong>read‑only</strong> manner to generate analytics and reports. This policy explains
          what data we access, how it is used, and how it is stored.
        </p>
      </Section>
      <Section title="Data We Access">
        <p>
          When you connect a credential (a HubSpot Private App token, Service Key, or Personal Access
          Key) or upload a CSV, SalesTap reads the CRM data needed for analysis — such as deals,
          pipelines, owners, activities, and contacts. We access this data <strong>only to fetch and
          analyze it for you</strong>. We never receive or store your CRM password, and access is
          read‑only.
        </p>
      </Section>
      <Section title="Ephemeral Storage — No Long‑Term Retention">
        <p>
          Your CRM data and generated reports are stored <strong>temporarily</strong> only for the
          duration of your working session. Each browser session is isolated and its data is:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>cleared immediately when you click <strong>Disconnect</strong>;</li>
          <li>cleared shortly after you close the browser tab; and</li>
          <li>automatically deleted after <strong>~90 minutes of inactivity</strong> via database
            time‑to‑live expiry.</li>
        </ul>
        <p>Nothing is retained on our servers after your session ends. On each new visit you re‑enter
          your credential or re‑upload your CSV.</p>
      </Section>
      <Section title="AI Processing">
        <p>
          To produce written analyses and answer your questions, computed summaries of your data are
          sent to a third‑party large language model provider (Anthropic Claude, via the Emergent
          platform) to generate the report text. Only the aggregated/analytical context needed for the
          report is sent; it is processed to generate your output and is not used by us to build any
          long‑term profile.
        </p>
      </Section>
      <Section title="Third‑Party Services">
        <p>
          SalesTap integrates with HubSpot (your CRM) and uses an AI provider as described above. Your
          use of those services remains subject to their respective terms and privacy policies.
        </p>
      </Section>
      <Section title="Security">
        <p>
          Credentials are transmitted over encrypted connections and used solely to fetch your CRM
          data. Because data is ephemeral and session‑scoped, it is not accumulated in long‑term
          storage. No system is perfectly secure, and you use the service at your own risk.
        </p>
      </Section>
      <Section title="Your Choices">
        <p>
          You can disconnect and clear your data at any time using the in‑app controls. You control
          which read scopes your credential grants.
        </p>
      </Section>
      <Section title="Contact">
        <p>
          Questions about this policy? Contact us at <strong>[your‑contact@example.com]</strong>
          {" "}([Company Legal Name], [Jurisdiction]).
        </p>
      </Section>
    </LegalShell>
  );
}

export function TermsOfServiceDialog({ open, onOpenChange }) {
  return (
    <LegalShell open={open} onOpenChange={onOpenChange} title="Terms of Service" subtitle="SalesTap">
      <Section title="Acceptance of Terms">
        <p>
          By accessing or using SalesTap, you agree to these Terms of Service. If you do not agree, do
          not use the service.
        </p>
      </Section>
      <Section title="The Service">
        <p>
          SalesTap provides read‑only analysis of your CRM data, including dashboards, activity and
          relationship insights, and AI‑generated reports. You are responsible for obtaining a valid
          CRM credential with appropriate read scopes and for your use of the outputs.
        </p>
      </Section>
      <Section title="Your Responsibilities">
        <ul className="list-disc pl-5 space-y-1">
          <li>You must have authorization to access and analyze the CRM data you connect.</li>
          <li>You are responsible for safeguarding your credentials and for activity under your session.</li>
          <li>You will not misuse, reverse‑engineer, or attempt to disrupt the service.</li>
        </ul>
      </Section>
      <Section title="AI‑Generated Content Disclaimer">
        <p>
          Reports, forecasts, and answers are generated by an automated AI model from your data and may
          contain errors or omissions. They are provided for informational purposes only and are
          <strong> not professional, financial, or legal advice</strong>. Verify important figures
          against your source system before making decisions.
        </p>
      </Section>
      <Section title="Data Handling">
        <p>
          As described in the Privacy Policy, CRM data and reports are stored ephemerally per session
          and are automatically deleted; SalesTap does not retain your CRM data after your session ends.
        </p>
      </Section>
      <Section title="Disclaimer of Warranties">
        <p>
          The service is provided “as is” and “as available,” without warranties of any kind, express or
          implied, including fitness for a particular purpose and non‑infringement.
        </p>
      </Section>
      <Section title="Limitation of Liability">
        <p>
          To the maximum extent permitted by law, SalesTap and [Company Legal Name] will not be liable
          for any indirect, incidental, or consequential damages, or for decisions made based on the
          service&rsquo;s outputs.
        </p>
      </Section>
      <Section title="Changes & Termination">
        <p>
          We may update these terms or the service at any time. Continued use after changes constitutes
          acceptance. You may stop using the service at any time.
        </p>
      </Section>
      <Section title="Governing Law & Contact">
        <p>
          These terms are governed by the laws of [Jurisdiction]. Questions? Contact
          {" "}<strong>[your‑contact@example.com]</strong> ([Company Legal Name]).
        </p>
      </Section>
    </LegalShell>
  );
}
